import { useState, useEffect } from "react";
import { useParallax } from "../hooks/useParallax";
import { Button } from "./ui/button";
import { ArrowRight, Play } from "lucide-react";

export const HeroSection = () => {
  const scrollY = useParallax();
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleScroll = (e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden pt-[72px]">
      {/* Animated background orbs */}
      <div className="absolute top-10 -left-40 w-[550px] h-[550px] bg-indigo-200/40 rounded-full blur-[130px] pointer-events-none orb-float-1" />
      <div className="absolute bottom-0 right-0 w-[450px] h-[450px] bg-violet-200/30 rounded-full blur-[110px] pointer-events-none orb-float-2" />
      <div className="absolute top-[40%] left-[40%] w-[500px] h-[500px] bg-indigo-100/25 rounded-full blur-[150px] pointer-events-none orb-float-3" />
      <div className="absolute top-[60%] right-[20%] w-[200px] h-[200px] bg-emerald-100/20 rounded-full blur-[80px] pointer-events-none orb-pulse" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Copy with staggered animation */}
          <div className="max-w-xl">
            <div
              className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-50/80 backdrop-blur-sm border border-indigo-100/50 mb-8 transition-all duration-700 ease-out ${
                loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: "200ms" }}
            >
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-xs font-semibold text-indigo-700 tracking-wide uppercase">
                Now in Early Access
              </span>
            </div>

            {/* Staggered headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-black leading-[1.08] tracking-tight text-slate-900">
              <span
                className={`block transition-all duration-700 ease-out ${
                  loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                }`}
                style={{ transitionDelay: "350ms" }}
              >
                Stop Chasing Tenders.
              </span>
              <span
                className={`block text-indigo-600 transition-all duration-700 ease-out ${
                  loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                }`}
                style={{ transitionDelay: "550ms" }}
              >
                Start Winning Them.
              </span>
            </h1>

            <p
              className={`mt-6 text-lg text-slate-500 leading-relaxed max-w-lg font-body transition-all duration-700 ease-out ${
                loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: "750ms" }}
            >
              IndiBids uses AI to find, analyze, and build winning bid documents
              for Indian government tenders, in minutes, not days.
            </p>

            <div
              className={`mt-10 flex flex-wrap items-center gap-4 transition-all duration-700 ease-out ${
                loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
              style={{ transitionDelay: "950ms" }}
            >
              <Button
                onClick={(e) => handleScroll(e, "#contact")}
                className="btn-glow bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-7 h-12 rounded-lg shadow-none text-[0.95rem] group"
              >
                Request Early Access
                <ArrowRight size={16} className="ml-2 transition-transform duration-300 group-hover:translate-x-1.5" />
              </Button>
              <button
                onClick={(e) => handleScroll(e, "#how-it-works")}
                className="inline-flex items-center gap-2 px-5 h-12 text-[0.95rem] font-medium text-slate-600 hover:text-indigo-600 transition-colors duration-200 group"
              >
                <span className="w-9 h-9 rounded-full bg-indigo-50/80 backdrop-blur-sm border border-indigo-100/50 flex items-center justify-center transition-all duration-300 group-hover:bg-indigo-100 group-hover:scale-110">
                  <Play size={14} className="text-indigo-600 ml-0.5" />
                </span>
                See How It Works
              </button>
            </div>
          </div>

          {/* Right: Dashboard Mockup with parallax */}
          <div
            className={`relative lg:flex lg:items-center lg:justify-end transition-all duration-1000 ease-out ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
            }`}
            style={{
              transitionDelay: "600ms",
              transform: loaded
                ? `translateY(${scrollY * -0.04}px)`
                : "translateY(48px)",
            }}
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-indigo-200/50 border border-slate-200/60 bg-white/80 backdrop-blur-sm hover-lift max-w-[480px] lg:max-w-[520px] mx-auto lg:mx-0">
              {/* Browser Chrome */}
              <div className="flex items-center gap-2 px-3 py-2.5 bg-slate-50/80 border-b border-slate-100">
                <div className="flex gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-300" />
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-300" />
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-300" />
                </div>
                <div className="flex-1 mx-2">
                  <div className="bg-white rounded-md px-2.5 py-0.5 text-[11px] text-slate-400 border border-slate-200 w-full max-w-[200px]">
                    app.indibids.com
                  </div>
                </div>
              </div>
              {/* Dashboard Screenshot */}
              <img
                src="/indibids-dashboard-new.png"
                alt="IndiBids Dashboard"
                className="w-full h-auto object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
